import {
  Table, Button, Space, Tag, message, Popconfirm, Badge, Card, Statistic, Row, Col
} from 'antd';
import { DeleteOutlined, SyncOutlined, CloudServerOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { agentsApi } from '../../api/agents';
import { FirmEnrollmentsPanel } from './FirmEnrollmentsPanel';
import { MirrorOnboardingCard } from './MirrorOnboardingCard';
import { MirrorFreshnessCard } from './MirrorFreshnessCard';
import type { Agent, AgentBackend } from '../../types';
import { AGENT_BACKEND } from '../../types';

export function FirmAgentsPanel({ firmId, agentBackend, ittihatTenantId, erpType }: {
  firmId: number; agentBackend?: AgentBackend; ittihatTenantId?: string | null; erpType?: string;
}) {
  if (agentBackend === AGENT_BACKEND.IttihatAgent) {
    return (
      <>
        <FirmEnrollmentsPanel firmId={firmId} tenantId={ittihatTenantId} erpType={erpType} />
        {/* 🔴 İttihat/CDC firmalarının `agents` satırı YOK — aşağıdaki LegacyAgentsPanel'in
            çevrimiçi/çevrimdışı rozeti onlar için hiç çalışmaz ve izleme sayfası da aynı ölü
            tablodan besleniyor. Bu kart, o firmaların sağlığını gösteren TEK yüzey. */}
        <MirrorFreshnessCard firmId={firmId} />
        <MirrorOnboardingCard firmId={firmId} tenantId={ittihatTenantId} />
      </>
    );
  }
  return <LegacyAgentsPanel firmId={firmId} />;
}

function LegacyAgentsPanel({ firmId }: { firmId: number }) {
  const queryClient = useQueryClient();

  const { data: agents = [], isLoading } = useQuery({
    queryKey: ['agents', firmId],
    queryFn: () => firmId ? agentsApi.getByFirm(firmId) : Promise.resolve([]),
    enabled: !!firmId,
  });

  const deleteMutation = useMutation({
    mutationFn: ({ firmId: fid, id }: { firmId: number; id: number }) =>
      agentsApi.delete(fid, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agents'] });
      message.success('Agent silindi');
    },
    onError: () => message.error('Agent silinemedi'),
  });

  const onlineAgents = agents.filter(a => a.isOnline).length;
  const totalAgents = agents.length;

  const columns = [
    {
      title: 'Agent Adı',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, record: Agent) => (
        <Space>
          <Badge status={record.isOnline ? 'success' : 'default'} />
          {name}
        </Space>
      ),
    },
    {
      title: 'Durum',
      key: 'status',
      render: (_: unknown, record: Agent) => (
        <Tag color={record.isOnline ? 'green' : 'default'}>
          {record.isOnline ? 'Çevrimiçi' : 'Çevrimdışı'}
        </Tag>
      ),
    },
    {
      title: 'API Key',
      dataIndex: 'apiKey',
      key: 'apiKey',
      render: (apiKey: string) => (
        <code style={{ fontSize: '11px' }}>{apiKey.substring(0, 20)}...</code>
      ),
    },
    {
      title: 'Son Görülme',
      dataIndex: 'lastSeenAt',
      key: 'lastSeenAt',
      render: (date: string | null) =>
        date ? new Date(date).toLocaleString('tr-TR') : '-',
    },
    {
      title: 'Kayıt Tarihi',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => new Date(date).toLocaleDateString('tr-TR'),
    },
    {
      title: 'İşlemler',
      key: 'actions',
      render: (_: unknown, record: Agent) => (
        <Space>
          <Popconfirm
            title="Agent'ı silmek istediğinize emin misiniz?"
            description="Bu işlem agent'ı deaktif eder."
            onConfirm={() => deleteMutation.mutate({
              firmId,
              id: record.id
            })}
            okText="Evet"
            cancelText="Hayır"
          >
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={8}>
          <Card>
            <Statistic
              title="Toplam Agent"
              value={totalAgents}
              prefix={<CloudServerOutlined />}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title="Çevrimiçi"
              value={onlineAgents}
              valueStyle={{ color: '#3f8600' }}
              prefix={<SyncOutlined spin={onlineAgents > 0} />}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title="Çevrimdışı"
              value={totalAgents - onlineAgents}
              valueStyle={{ color: totalAgents - onlineAgents > 0 ? '#cf1322' : undefined }}
            />
          </Card>
        </Col>
      </Row>

      <Table
        columns={columns}
        dataSource={agents}
        rowKey="id"
        loading={isLoading}
        locale={{ emptyText: 'Bu firmaya ait agent bulunamadı' }}
      />
    </div>
  );
}
